import { Component, inject, OnInit } from '@angular/core';
import createDebug from 'debug';
import { PackageService } from './services/package/package.service';
import { AuthService } from './services/auth/auth.service';
import { PageService } from './services/page/page.service';
import { AsyncPipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatListItem,
  MatListItemIcon,
  MatNavList,
} from '@angular/material/list';
import {
  MatSidenav,
  MatSidenavContainer,
  MatSidenavContent,
} from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import {
  FlexColumnComponent,
  FlexRowComponent,
  GrowDirective,
} from '@apexdesigner/flex-layout';
import { AvatarComponent } from '@components/avatar/avatar.component';
import { AdElDirective } from './directives/ad-el/ad-el.directive';

const Debug = createDebug('AppComponent');

/**
 * App
 *
 * Root application component.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: { adComp: '' },
  imports: [
    AdElDirective,
    AsyncPipe,
    AvatarComponent,
    FlexColumnComponent,
    FlexRowComponent,
    GrowDirective,
    MatIcon,
    MatIconButton,
    MatListItem,
    MatListItemIcon,
    MatNavList,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    MatToolbar,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit {
  packageService = inject(PackageService);
  /** Sidenav Open */
  sidenavOpen = false;

  /** Toggles the sidenav and drops focus from the toggle button so it doesn't stay highlighted. */
  toggleSidenav(event: Event) {
    // Flip the sidenav and clear focus from the toggle button
    this.sidenavOpen = !this.sidenavOpen;
    (event.currentTarget as HTMLElement).blur();
  }

  authService = inject(AuthService);
  pageService = inject(PageService);
  /**
   * Visible Sidenav Pages - Sidenav pages the current user's roles allow.
   * Typed via PageService because the client component generator only carries
   * service-class imports from `@services`, not type-only ones.
   */
  visibleSidenavPages: PageService['sidenavPages'] = [];

  /** Initialize - Recompute the visible sidenav pages whenever the current user changes */

  async initialize(): Promise<void> {
    this.authService.currentUser.subscribe((user) => {
      // Mirrors the roleGuard semantics: a page with no declared roles is
      // visible to any resolved user, the built-in Everyone role is public
      // (the routes generator strips it before the guard ever sees it), and
      // otherwise any one matching role assignment suffices.
      this.visibleSidenavPages = this.pageService.sidenavPages.filter(
        (page) =>
          page.roles.length === 0 ||
          page.roles.includes('Everyone') ||
          page.roles.some((roleName) =>
            user?.roleAssignments?.some(
              (ra: any) => ra.role?.name === roleName,
            ),
          ),
      );
    });
  }

  ngOnInit(): void {
    this.initialize();
  }
}

// Named seams below form AppComponent's public refinement surface. See
// docs/patterns/app-shell-seams.md before renaming any `name:` — a rename is a
// breaking refinement-surface change for downstream `app.component.patch.ts`.
